import { useEffect } from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useToastStore, type Toast } from "@/hooks/useToast";

/**
 * Durée d'affichage par défaut d'un toast avant auto-dismiss (ms).
 * Suffisant pour lire « X simulations rattachées » sans gêner la navigation.
 */
const TOAST_TTL_MS = 5000;

/**
 * Une notification transitoire. Auto-dismiss après {@link TOAST_TTL_MS} ;
 * dismiss manuel possible via la croix. `role="status"` + `aria-live="polite"`
 * pour l'annonce aux lecteurs d'écran (le viewport porte déjà le live region).
 */
function ToastItem({ toast }: { toast: Toast }): JSX.Element {
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), TOAST_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, dismiss]);

  return (
    <div
      className={cn(
        "pointer-events-auto flex items-start gap-3 rounded-md px-4 py-3 text-sm shadow-lg",
        toast.variant === "success"
          ? "bg-emerald-600 text-white"
          : "bg-rose-600 text-white",
      )}
    >
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="shrink-0 rounded text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label="Fermer la notification"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Conteneur unique des toasts, monté dans `AppLayout`. Empilés en bas à droite,
 * au-dessus de tout le reste (Leaflet pose des panes jusqu'à z=1000, modales à
 * z=1100 — un toast doit rester visible y compris par-dessus une modale).
 */
export function ToastViewport(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[1200] flex w-full max-w-sm flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
